import { Button, Group, Paper, Portal, Stack, Text } from '@mantine/core';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DEMO_COACH_COPY } from '@creativitas/shared';
import type { DemoCoachId } from './demo-step';

/**
 * Контекстные подсказки демо (ТЗ демо, п. 7.1).
 *
 * Лёгкий «прожектор»: экран слегка затемняется, нужный элемент остаётся
 * светлым, рядом — короткое пояснение. Подсказка появляется, когда действие
 * впервые нужно, и больше не повторяется.
 *
 * Затемнение не перехватывает нажатия: оно нарисовано тенью элемента без
 * `pointer-events`, поэтому подсвеченную кнопку можно нажать сразу, а
 * подсказка не может запереть приложение — даже если цель скрыта или
 * поле прокручено.
 *
 * Прогресс обучения хранится в браузере по пользователю и сценарию. От него
 * не зависит ничего в самой партии: стёртое хранилище означает только, что
 * подсказки покажутся снова.
 */

type CoachState = { seen: string[]; skipped: boolean };

const EMPTY: CoachState = { seen: [], skipped: false };

function read(key: string): CoachState {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<CoachState>;
    return { seen: Array.isArray(parsed.seen) ? parsed.seen : [], skipped: parsed.skipped === true };
  } catch {
    return EMPTY;
  }
}

function write(key: string, state: CoachState) {
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* Приватный режим или запрет хранилища — обучение просто покажется снова. */
  }
}

export function useDemoCoach(storageKey: string) {
  const [state, setState] = useState<CoachState>(() => read(storageKey));
  useEffect(() => setState(read(storageKey)), [storageKey]);

  const update = useCallback((next: (current: CoachState) => CoachState) => {
    setState((current) => {
      const value = next(current);
      write(storageKey, value);
      return value;
    });
  }, [storageKey]);

  return {
    seen: state.seen,
    skipped: state.skipped,
    markSeen: useCallback((id: string) => update((current) =>
      current.seen.includes(id) ? current : { ...current, seen: [...current.seen, id] }), [update]),
    skipAll: useCallback(() => update((current) => ({ ...current, skipped: true })), [update]),
    reset: useCallback(() => update(() => EMPTY), [update]),
  };
}

export type DemoCoachControls = ReturnType<typeof useDemoCoach>;

/**
 * Какая подсказка сейчас уместна: сначала поле — один раз в начале, затем
 * подсказка текущего шага, а после первого ресурса — где ресурсы лежат.
 */
export function pickCoach(
  coach: DemoCoachControls,
  stepCoach: DemoCoachId | null,
  hasResources: boolean,
): DemoCoachId | null {
  if (coach.skipped) return null;
  const fresh = (id: DemoCoachId) => !coach.seen.includes(id);
  if (fresh('board')) return 'board';
  if (stepCoach && fresh(stepCoach)) return stepCoach;
  if (hasResources && fresh('resources')) return 'resources';
  return null;
}

type Rect = { top: number; left: number; width: number; height: number };

const PAD = 6;

export function DemoCoach({ coachId, coach }: { coachId: DemoCoachId | null; coach: DemoCoachControls }) {
  const [rect, setRect] = useState<Rect | null>(null);
  const [blocked, setBlocked] = useState(false);
  const previous = useRef<DemoCoachId | null>(null);
  const markSeen = useRef(coach.markSeen);
  markSeen.current = coach.markSeen;

  /* Действие сделано, шаг сменился — подсказка о нём больше не нужна. */
  useEffect(() => {
    const before = previous.current;
    if (before && before !== coachId && before !== 'board' && before !== 'resources') {
      markSeen.current(before);
    }
    previous.current = coachId;
  }, [coachId]);

  /* Цель могла уехать за край экрана: показываем её, а не текст в пустоте. */
  useEffect(() => {
    if (!coachId) return;
    const target = document.querySelector<HTMLElement>(`[data-coach="${coachId}"]`);
    const box = target?.getBoundingClientRect();
    /* Прокручиваем, только если цель не видна: страница не должна ехать из-под курсора. */
    const visible = box && box.top >= 0 && box.bottom <= window.innerHeight;
    if (target && !visible) target.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [coachId]);

  useLayoutEffect(() => {
    if (!coachId) return undefined;
    let frame = 0;
    let last = '';
    const measure = () => {
      /* Пока открыт диалог (сказка, карта), подсказка ждёт: два слоя внимания — это шум. */
      const modal = Boolean(document.querySelector('.mantine-Modal-content'));
      const target = document.querySelector<HTMLElement>(`[data-coach="${coachId}"]`);
      const box = target?.getBoundingClientRect();
      const next = box && box.width > 0 && box.height > 0
        ? { top: box.top, left: box.left, width: box.width, height: box.height }
        : null;
      /* Перерисовываем только при сдвиге: замер идёт каждый кадр, рендер — нет. */
      const signature = `${modal}|${next ? `${next.top}|${next.left}|${next.width}|${next.height}` : '-'}`;
      if (signature !== last) {
        last = signature;
        setBlocked(modal);
        setRect(next);
      }
      frame = window.requestAnimationFrame(measure);
    };
    measure();
    return () => window.cancelAnimationFrame(frame);
  }, [coachId]);

  /*
    Фокус подсказка не забирает: она появляется в тот момент, когда человек
    уже тянется к кнопке, и перехват закрывал бы ему меню или поле ввода.
    Escape работает всегда, «Понятно» доступна по Tab.
  */
  useEffect(() => {
    if (!coachId || blocked) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') markSeen.current(coachId);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [coachId, blocked]);

  if (!coachId || blocked) return null;
  const copy = DEMO_COACH_COPY[coachId];
  if (!copy) return null;

  /*
    Карточка — под целью или над ней, где хватает места. Если цель выше экрана
    (поле на ноутбуке), места нет ни там, ни там: тогда карточка встаёт в угол
    экрана и не уезжает за край.
  */
  const viewportHeight = typeof window === 'undefined' ? 800 : window.innerHeight;
  const CARD_HEIGHT = 190;
  const spaceBelow = rect ? viewportHeight - (rect.top + rect.height + PAD * 2) : 0;
  const spaceAbove = rect ? rect.top - PAD * 2 : 0;
  const left = rect ? Math.max(16, Math.min(rect.left, window.innerWidth - 376)) : 16;
  /* Большую цель (поле) карточка не обходит сверху — там соседние кнопки. */
  const tall = rect ? rect.height > viewportHeight * 0.5 : false;
  const cardStyle: React.CSSProperties = rect && !tall && spaceBelow >= CARD_HEIGHT
    ? { position: 'fixed', left, top: rect.top + rect.height + PAD * 2 }
    : rect && !tall && spaceAbove >= CARD_HEIGHT
      ? { position: 'fixed', left, bottom: viewportHeight - rect.top + PAD * 2 }
      : { position: 'fixed', right: 16, bottom: 16 };

  /*
    В портал, к корню документа: у оболочки приложения есть предки с
    трансформацией, а внутри них `position: fixed` отсчитывается от предка, и
    подсветка съезжает с кнопки.
  */
  return (
    <Portal>
      {rect ? (
        <div
          aria-hidden
          data-testid="demo-coach-spotlight"
          style={{
            position: 'fixed',
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            borderRadius: 12,
            boxShadow: '0 0 0 9999px rgba(10, 32, 31, 0.42)',
            outline: '2px solid var(--gold)',
            pointerEvents: 'none',
            zIndex: 180,
            transition: 'top 120ms ease, left 120ms ease, width 120ms ease, height 120ms ease',
          }}
        />
      ) : null}
      <Paper
        role="dialog"
        aria-label={copy.title}
        data-testid="demo-coach"
        withBorder
        shadow="md"
        p="md"
        style={{ ...cardStyle, width: 'min(360px, calc(100vw - 32px))', zIndex: 181 }}
      >
        <Stack gap="xs">
          <Text fw={700}>{copy.title}</Text>
          <Text size="sm">{copy.text}</Text>
          <Group justify="space-between" mt={4}>
            <Button size="compact-sm" variant="subtle" color="gray" onClick={coach.skipAll}>
              Пропустить обучение
            </Button>
            <Button size="compact-sm" onClick={() => coach.markSeen(coachId)}>
              Понятно
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Portal>
  );
}
